import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Plus, FolderOpen, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', color: '#3B82F6', icon: 'folder' });
  const [loading, setLoading] = useState(false);

  const fetchCategories = () => api.get('/documents/categories').then(r => setCategories(r.data.categories));
  useEffect(() => { fetchCategories(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/documents/categories/create', form);
      toast.success('Category created');
      setForm({ name: '', description: '', color: '#3B82F6', icon: 'folder' });
      setShowForm(false);
      fetchCategories();
    } catch (e) {}
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-500 text-sm">Organize documents into categories</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> New Category
        </button>
      </div>

      {showForm && (
        <div className="card border-2 border-primary-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Create Category</h2>
            <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Name *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required />
              </div>
              <div>
                <label className="label">Color</label>
                <div className="flex gap-2">
                  <input type="color" className="h-10 w-16 rounded border border-gray-300 cursor-pointer p-1"
                    value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} />
                  <input className="input flex-1" value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} />
                </div>
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <input className="input" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.length === 0 && (
          <div className="col-span-3 card text-center py-12 text-gray-400">
            <FolderOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No categories yet. Create one to organize documents.</p>
          </div>
        )}
        {categories.map(c => (
          <Link key={c.id} to={`/app/categories/${c.id}`} className="card flex items-center gap-4 hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
              style={{ backgroundColor: c.color }}>
              <FolderOpen className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{c.name}</p>
              {c.description && <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/documents/audit-logs').then(r => { setLogs(r.data.logs); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const actionColors = {
    DOCUMENT_CREATED: 'bg-green-100 text-green-700',
    DOCUMENT_UPDATED: 'bg-blue-100 text-blue-700',
    DOCUMENT_DELETED: 'bg-red-100 text-red-700',
    DOCUMENT_DOWNLOADED: 'bg-purple-100 text-purple-700',
    DOCUMENT_VIEWED: 'bg-gray-100 text-gray-600',
    USER_CREATED: 'bg-emerald-100 text-emerald-700',
    USER_DEACTIVATED: 'bg-orange-100 text-orange-700',
    VERSION_UPLOADED: 'bg-indigo-100 text-indigo-700',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="text-gray-500 text-sm">Complete activity trail for your workspace</p>
      </div>

      <div className="card !p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Action', 'User', 'Resource', 'IP Address', 'Time'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">No audit logs yet</td></tr>
              ) : logs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>
                      {log.action.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-medium text-xs">{log.user_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                    {log.resource_type}/{log.resource_id?.slice(0, 8)}...
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">{log.ip_address || '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
